/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Controller,
} from 'react-hook-form';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  useForm,
  getFieldProps,
  getFormProps,
  MultiCountrySelector,
  OutlinedIcons,
  ZAvatar,
  Link,
} from '@marageti/z4-lib';
import { PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { CurrencyAmount } from '@marageti/z4-sdk/lib/standards';
import { addYears } from 'date-fns';
import CurrencyInput from '../../currency-input';
import ReadOnlyCurrencySelect from '../../read-only-currency-select';
import config from '../../../config';
import CommissionInfo from '../../commission-info';
import { isCanceledSale } from '../../../utils/sales';

type FormDefaultValues = {
  countries: string[] | undefined,
  departureDate: Date | undefined,
  totalNights: number | undefined,
  numberOfAdults: number | undefined,
  numberOfChildren: number | undefined,
  price: Partial<CurrencyAmount> | undefined,
  commission: Partial<CurrencyAmount> | undefined,
  priceComments: string | undefined,
  document?: any[] | undefined,
};

const DEFAULT_VALUES = {
  countries: [],
  departureDate: undefined,
  totalNights: undefined,
  numberOfAdults: undefined,
  numberOfChildren: undefined,
  price: {
    amount: 0,
    currencyCode: 'USD',
  },
  commission: {
    amount: 0,
    currencyCode: 'USD',
  },
  priceComments: '',
} as FormDefaultValues;

const createSchema = (totalsOfSalesRecordings: {
  price: { amount: number },
  commission: { amount: number }
}) => z.object({
  countries: z.array(z.string()).min(1, { message: 'Required' }),
  departureDate: z.date({ required_error: 'Enter the departure Date' }),
  totalNights: z.coerce.number().min(1, 'Enter the total number of nights'),
  numberOfAdults: z.coerce.number().min(1, 'Enter the number of adults'),
  numberOfChildren: z.coerce.number().min(0, 'Enter the number of children'),
  price: z.object({
    amount: z.number().max(9999999, 'Trip Price cannot exceed 9,999,999'),
    currencyCode: z.string(),
  }),
  commission: z.object({
    amount: z.number().max(9999999, 'Commission cannot exceed 9,999,999'),
    currencyCode: z.string(),
  }),
  priceComments: z.string().min(1, { message: 'Required' }),
  document: z
    .any()
    // Optional but if provided, must be a file with size <= 50 MB
    .refine((files) => files === undefined || files.length === 0 || files[0]?.size <= 50 * 1024 * 1024, 'Max file size is 50 MB')
    // Only PDF, TXT, DOC, DOCX
    .refine(
      (files) => files === undefined || files.length === 0 || [
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(files?.[0]?.type),
      'Only .pdf, .txt, .doc, .docx files are allowed',
    ),
}).superRefine((data, ctx) => {
  const priceAdjustment = data.price.amount;
  const commissionAdjustment = data.commission.amount;
  const finalTripPrice = totalsOfSalesRecordings.price.amount + priceAdjustment;
  const finalCommission = totalsOfSalesRecordings.commission.amount + commissionAdjustment;

  if (finalTripPrice < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The final trip price cannot be negative.',
      path: ['price', 'amount'],
    });
  }
  if (finalCommission < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'The final commission cannot be negative.',
      path: ['commission', 'amount'],
    });
  }
});

type FormProps = {
  primaryTraveler: Traveler | undefined,
  countries: PartnerCountry[] | undefined,
  defaultValues: {
    countries: string[] | undefined,
    departureDate: Date | undefined,
    totalNights: number | undefined,
    numberOfAdults: number | undefined,
    numberOfChildren: number | undefined,
    price: Partial<CurrencyAmount> | undefined,
    commission: Partial<CurrencyAmount> | undefined,
    priceComments: string | undefined,
    document?: any[] | undefined,
  },
  totalsOfSalesRecordings: {
    price: {
      amount: number
    },
    commission: {
      amount: number
    }
  },
  tripMatch: any,
  onSubmit: (data: any) => void,
  submissionError: boolean
  onCancel?: () => void
};

const Form = ({
  primaryTraveler,
  countries,
  defaultValues,
  totalsOfSalesRecordings,
  tripMatch,
  onSubmit,
  submissionError,
  onCancel = undefined,
}: FormProps) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    watch,
    setValue,
    trigger,
  } = useForm({
    resolver: zodResolver(createSchema(totalsOfSalesRecordings)),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const currency = defaultValues.price?.currencyCode;
  const tripPriceAdjustmentAmount = watch('price.amount');
  const newTripPriceAmount = totalsOfSalesRecordings.price.amount + (tripPriceAdjustmentAmount || 0);
  const commissionAdjustmentAmount = watch('commission.amount');
  const newCommissionAmount = totalsOfSalesRecordings.commission.amount + (commissionAdjustmentAmount || 0);
  const maxDate = addYears(new Date(), 10);
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const selectedDocument = watch('document');
  const hasDocument = selectedDocument && selectedDocument.length > 0;
  // On canceled trips the trip details (countries, dates, pax) can't be persisted (the backend
  // forbids PUT /trip_matches), so we hide that section and keep only the adjustment fields editable.
  const hideTripDetails = isCanceledSale(tripMatch?.status);

  useEffect(() => {
    if (!isSubmitting) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSubmitting]);

  useEffect(() => {
    trigger('document');
  }, [selectedDocument]);

  const handleRemoveDocument = () => {
    setValue('document', undefined);
  };

  return (
    <form {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={2} useFlexGap>
        {!hideTripDetails && (
        <>
          <div>
            <Typography
              variant="h3"
              sx={{
                mb: 1,
              }}
            >
              Update Trip Details
            </Typography>
            <Typography color="text.secondary" mb={2}>
              Please update the sold trip details below. These details will be shown to the traveler.
            </Typography>
          </div>

          {primaryTraveler && (
            <Card variant="dense">
              <CardContent>
                <List disablePadding>
                  <ListItem dense disableGutters>
                    <ListItemAvatar>
                      <ZAvatar size={32} src={primaryTraveler.avatar} name={primaryTraveler.fullName} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={primaryTraveler.fullName}
                      primaryTypographyProps={{
                        variant: 'bodyBold',
                      }}
                      secondary="Primary Traveler"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}

          <Grid
            container
            columnSpacing={2}
          >
            <Grid item mobile={12} desktop={12}>
              <Controller
                name="countries"
                control={control}
                render={({ field }) => (
                  <MultiCountrySelector
                    {...field}
                    {...getFieldProps('countries', errors)}
                    label="Countries"
                    countries={countries}
                    required
                    onChange={(_: any, data: String[]) => {
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <Controller
                name="departureDate"
                control={control}
                render={({
                  field: {
                    onChange, onBlur, value, ref, name,
                  },
                }) => (
                  <DatePicker
                    label="Departure Date (MM/DD/YYYY)"
                    onChange={onChange}
                    value={value}
                    maxDate={maxDate}
                    slotProps={{
                      textField: {
                        name,
                        onBlur,
                        fullWidth: true,
                        required: true,
                        ...getFieldProps('departureDate', errors),
                      },
                    }}
                    inputRef={ref}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Nights"
                required
                {...register('totalNights')}
                {...getFieldProps('totalNights', errors)}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Adults"
                required
                {...register('numberOfAdults')}
                {...getFieldProps('numberOfAdults', errors)}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Children"
                {...register('numberOfChildren')}
                {...getFieldProps('numberOfChildren', errors)}
              />
            </Grid>
          </Grid>

          <Divider
            sx={{
              my: 2,
            }}
          />
        </>
        )}

        {/* Changes to Trip Price */}
        <div>

          <Stack spacing={1} mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h3"
              >
                Changes to Trip Price
              </Typography>
              {currency !== 'USD' && (
                <Tooltip
                  title="Please note that the traveler will see an approximate conversion to USD."
                  placement="right"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </Tooltip>
              )}
            </Stack>
            <Typography color="text.secondary">
              Please enter
              {' '}
              <strong>only the difference.</strong>
              {' '}
              The updated trip price will be shown to the traveler.
            </Typography>
          </Stack>

          <Grid container columnSpacing={2}>
            <Grid item mobile={4} desktop={2}>
              <ReadOnlyCurrencySelect
                {...register('price.currencyCode')}
                InputProps={{
                  defaultValue: defaultValues?.price?.currencyCode,
                }}
              />
            </Grid>

            <Grid item mobile={8} desktop={10}>
              <Controller
                control={control}
                name="price.amount"
                render={({ field: { onChange, value, ...field } }) => (
                  <CurrencyInput
                    label="Trip Price Adjustment"
                    fullWidth
                    required
                    error={!!errors.price?.amount}
                    helperText={errors.price?.amount?.message}
                    defaultValue={defaultValues?.price?.amount}
                    numericFormatProps={{
                      currencyCode: currency,
                    }}
                    {...field}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={12}>
              <Stack spacing={1}>
                <Box display="flex" flexDirection="column" sx={{ pl: 1 }}>
                  <Typography variant="caption">
                    • Current Trip Price:
                    {' '}
                    {currencyFormatter.format(totalsOfSalesRecordings.price.amount)}
                  </Typography>
                  <Box>
                    <Typography variant="caption">
                      • New Trip Price:
                      {' '}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={typeof tripPriceAdjustmentAmount === 'number' && tripPriceAdjustmentAmount >= 0 ? 'success.main' : 'error.main'}
                    >
                      {currencyFormatter.format(newTripPriceAmount)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption">
                  This is an adjustment to your previously recorded price. To indicate a subtraction from your original price, simply add a “-” before the amount. For example “-500”.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </div>

        <Divider
          sx={{
            my: 2,
          }}
        />

        {/* Changes to Commission to Zicasso */}
        <div>
          <Stack spacing={1} mb={2}>
            <Typography
              variant="h3"
            >
              Changes to Commission to Zicasso
            </Typography>
            <Typography color="text.secondary">
              Please enter
              {' '}
              <strong>only the difference.</strong>
              {' '}
              These details will
              {' '}
              <Typography component="span" variant="bodyBold">not be shown</Typography>
              {' '}
              to the traveler.
            </Typography>
          </Stack>
          <Grid container columnSpacing={2} mb={3}>
            <Grid item mobile={4} desktop={2}>
              <ReadOnlyCurrencySelect
                {...register('commission.currencyCode')}
                InputProps={{
                  defaultValue: defaultValues?.commission?.currencyCode,
                }}
              />
            </Grid>

            <Grid item mobile={8} desktop={10}>
              <Controller
                control={control}
                name="commission.amount"
                render={({ field: { onChange, value, ...field } }) => (
                  <CurrencyInput
                    label="Commission Adjustment"
                    fullWidth
                    required
                    error={!!errors.commission?.amount}
                    helperText={errors.commission?.amount?.message}
                    defaultValue={defaultValues?.commission?.amount}
                    numericFormatProps={{
                      currencyCode: currency,
                    }}
                    {...field}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item mobile={12} desktop={12}>
              <Stack spacing={1}>
                <Box display="flex" flexDirection="column" sx={{ pl: 1 }}>
                  <Typography variant="caption">
                    • Current Commission:
                    {' '}
                    {currencyFormatter.format(totalsOfSalesRecordings.commission.amount)}
                  </Typography>
                  <Box>
                    <Typography variant="caption">
                      • New Commission:
                      {' '}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={typeof commissionAdjustmentAmount === 'number' && commissionAdjustmentAmount >= 0 ? 'success.main' : 'error.main'}
                    >
                      {currencyFormatter.format(newCommissionAmount)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption">
                  This is an adjustment to your previously recorded commission to Zicasso.
                  To indicate a subtraction from your original commission, simply add a “-” before the amount. For example “-500”.
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Stack spacing={1} useFlexGap>
            <TextField
              label="Comments"
              multiline
              minRows={6}
              required
              {...register('priceComments')}
              {...getFieldProps('priceComments', errors)}
              placeholder="Describe the reason and calculation for this price/commission update (e.g., added hotel night, canceled tour...)"
            />
            <CommissionInfo tripMatch={tripMatch} tripPrice={{ amount: newTripPriceAmount, currencyCode: currency }} />
            <Typography variant="caption">
              Describe how you calculated the commission. Please see&nbsp;
              <Link sx={{ textDecoration: 'underline' }} to={`${config.Z3_URL}tc-terms-agreement`}>Travel Company Agreement</Link>
              &nbsp;(section 5) for specific details.
            </Typography>
          </Stack>
        </div>

        <Divider
          sx={{
            my: 2,
          }}
        />

        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 1,
            }}
          >
            Confirmed Itinerary / Quote (For
            {' '}
            Zicasso&apos;s Records)
          </Typography>
          <Typography color="text.secondary">
            Please submit an itinerary as proof of the services provided in connection with this sales recording.
            Files must not exceed 50 MB and can be the following file formats: txt, pdf, doc, and docx.
            {' '}
            <Typography component="span" variant="bodyBold">
              This file is NOT shared with the traveler.
            </Typography>
          </Typography>

          <Button
            component="label"
            variant="outlined"
            size="small"
            sx={{
              my: 2,
              display: hasDocument ? 'none' : 'inline-flex',
            }}
          >
            Browse...
            <input
              type="file"
              accept=".pdf,.txt,.doc,.docx"
              hidden
              {...register('document')}
            />
          </Button>

          {hasDocument && (
            <>
              <List
                disablePadding
              >
                <ListItem
                  disableGutters
                  secondaryAction={(
                    <IconButton
                      edge="end"
                      aria-label="remove document"
                      onClick={handleRemoveDocument}
                    >
                      <OutlinedIcons.OutlinedTrashIcon color="primary" />
                    </IconButton>
                  )}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <OutlinedIcons.OutlinedDocumentIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={selectedDocument[0].name}
                  />
                </ListItem>
              </List>
              {errors.document && (
                <Typography variant="caption" color="error">{errors.document.message}</Typography>
              )}
            </>
          )}
        </Box>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting the form.</AlertTitle>
            Please try again.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={3}
          sx={(theme) => ({
            [theme.breakpoints.down('desktop')]: {
              marginTop: 'auto',
              flexDirection: 'column-reverse',
              '& > :not(:first-of-type)': {
                margin: theme.spacing(0, 0, 2.5, 0),
              },
            },
          })}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </LoadingButton>

          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};

export default Form;
